import React, { useEffect, useState } from "react";
import { Edit, Save, X, Plus, RotateCw } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import {
    fetchDevices,
    fetchLocations,
    fetchWorkModes,
    fetchStatuses,
    fetchSockets,
    updateDevice,
    addDevice,
} from "../services/devices";

const buildSelectableOutlets = (allOutlets, freeOutlets, selectedOutletId) => {
    const normalizedSelectedId = selectedOutletId === "" || selectedOutletId === null || selectedOutletId === undefined
        ? null
        : Number(selectedOutletId);

    const options = [...freeOutlets];
    if (normalizedSelectedId !== null && !options.some((outlet) => Number(outlet.id) === normalizedSelectedId)) {
        const selectedOutlet = allOutlets.find((outlet) => Number(outlet.id) === normalizedSelectedId);
        if (selectedOutlet) {
            options.push(selectedOutlet);
        }
    }

    return options;
};

const DevicesOperators = () => {
    const [devices, setDevices] = useState([]);
    const [filteredDevices, setFilteredDevices] = useState([]);
    const [message, setMessage] = useState("");
    const [locations, setLocations] = useState([]);
    const [workModes, setWorkModes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [sockets, setSockets] = useState([]);
    const [availableSockets, setAvailableSockets] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("device_name");
    const [sortDir, setSortDir] = useState("asc");

    const [editingDeviceId, setEditingDeviceId] = useState(null);
    const [editedDevice, setEditedDevice] = useState({});
    const [newDevice, setNewDevice] = useState({
        device_name: "",
        brand: "",
        descriptions: "",
        price: "",
        qr_code: "",
        location_id: "",
        work_modes_id: "",
        socket_id: "",
        status: "",
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [expandedDeviceId, setExpandedDeviceId] = useState(null);

    const buildLocationLabel = (loc) => {
        if (!loc) return "";
        return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
    };

    useEffect(() => {
        loadData();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const loadData = async () => {
        try {
            const [devs, locs, wms, sts, scks] = await Promise.all([
                fetchDevices(),
                fetchLocations(),
                fetchWorkModes(),
                fetchStatuses(),
                fetchSockets(),
            ]);
            setDevices(devs);
            setLocations(locs);
            setWorkModes(wms);
            setStatuses(sts);
            setSockets(scks);
            // Filter available sockets: those not bound to any device
            const usedSocketIds = new Set(devs.filter(d => d.socket_id).map(d => d.socket_id));
            const available = scks.filter(s => !usedSocketIds.has(s.id));
            setAvailableSockets(available);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const handleEdit = (device) => {
        setEditingDeviceId(device.id);
        setExpandedDeviceId(device.id);
        setEditedDevice({
            ...device,
            location_id: device.location_id ? String(device.location_id) : "",
            work_modes_id: device.work_modes_id ? String(device.work_modes_id) : "",
            status: device.status ? String(device.status) : "",
            socket_id: device.socket_id ? String(device.socket_id) : "",
        });
    };

    const toggleDeviceExpanded = (deviceId, isEditing) => {
        if (isEditing) return;
        setExpandedDeviceId((prev) => (prev === deviceId ? null : deviceId));
    };

    const handleCancel = () => {
        setEditingDeviceId(null);
        setEditedDevice({});
    };

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = devices.filter((device) =>
            (device.device_name || "").toString().toLowerCase().includes(q) ||
            (device.brand || "").toString().toLowerCase().includes(q) ||
            (device.descriptions || "").toString().toLowerCase().includes(q)
        );
        setFilteredDevices(filtered);
    }, [devices, searchQuery]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredDevices) return [];
        const sorted = [...filteredDevices];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const handleSave = async (id) => {
        try {
            const payload = {
                ...editedDevice,
                location_id: editedDevice.location_id === "" ? null : parseInt(editedDevice.location_id),
                work_modes_id: editedDevice.work_modes_id === "" ? null : parseInt(editedDevice.work_modes_id),
                status: editedDevice.status === "" ? null : parseInt(editedDevice.status),
                socket_id: editedDevice.socket_id === "" ? null : parseInt(editedDevice.socket_id),
                price: editedDevice.price ? parseFloat(editedDevice.price) : 0,
            };

            await updateDevice(id, payload);
            setEditingDeviceId(null);
            loadData();
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const handleAddDevice = async () => {
        if (!newDevice.device_name.trim() || !newDevice.brand.trim()) {
            alert("Всі обов’язкові поля мають бути заповнені!");
            return;
        }

        try {
            const payload = {
                ...newDevice,
                location_id: newDevice.location_id !== "" ? parseInt(newDevice.location_id) : undefined,
                work_modes_id: newDevice.work_modes_id !== "" ? parseInt(newDevice.work_modes_id) : undefined,
                status: newDevice.status !== "" ? parseInt(newDevice.status) : undefined,
                socket_id: newDevice.socket_id !== "" ? parseInt(newDevice.socket_id) : undefined,
                price: newDevice.price !== "" ? parseFloat(newDevice.price) : 0,
            };

            await addDevice(payload);
            setShowAddModal(false);
            setNewDevice({
                device_name: "",
                brand: "",
                descriptions: "",
                price: "",
                qr_code: "",
                location_id: "",
                work_modes_id: "",
                socket_id: "",
                status: "",
            });
            loadData();
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    return (
        <div>
            <div className="config-header">
                <h2>Пристрої</h2>
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadData} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук пристрою"
                    />
                </div>
            </div>

            {message && <p>{message}</p>}

            {/* Модалка додавання */}
            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content operator-mobile-modal operator-form-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати пристрій</h3>
                        <div className="operator-form-grid">
                            <input
                                type="text"
                                placeholder="Назва пристрою"
                                value={newDevice.device_name}
                                onChange={(e) => setNewDevice({ ...newDevice, device_name: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="Бренд"
                                value={newDevice.brand}
                                onChange={(e) => setNewDevice({ ...newDevice, brand: e.target.value })}
                            />
                            <input
                                className="operator-form-span-2"
                                type="text"
                                placeholder="Опис"
                                value={newDevice.descriptions}
                                onChange={(e) => setNewDevice({ ...newDevice, descriptions: e.target.value })}
                            />
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Ціна"
                                value={newDevice.price}
                                onChange={(e) => setNewDevice({ ...newDevice, price: e.target.value })}
                            />
                            <input
                                type="text"
                                placeholder="QR код"
                                value={newDevice.qr_code}
                                onChange={(e) => setNewDevice({ ...newDevice, qr_code: e.target.value })}
                            />

                            <select
                                value={newDevice.location_id}
                                onChange={(e) => setNewDevice({ ...newDevice, location_id: e.target.value })}
                            >
                                <option value="">Оберіть локацію</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>{buildLocationLabel(loc) || loc.location_name}</option>
                                ))}
                            </select>
                            <select
                                value={newDevice.work_modes_id}
                                onChange={(e) => setNewDevice({ ...newDevice, work_modes_id: e.target.value })}
                            >
                                <option value="">Оберіть режим</option>
                                {workModes.map((wm) => (
                                    <option key={wm.id} value={wm.id}>{wm.work_mode_name}</option>
                                ))}
                            </select>
                            <select
                                value={newDevice.socket_id}
                                onChange={(e) => setNewDevice({ ...newDevice, socket_id: e.target.value })}
                            >
                                <option value="">Оберіть розетку</option>
                                {availableSockets.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                            <select
                                value={newDevice.status}
                                onChange={(e) => setNewDevice({ ...newDevice, status: e.target.value })}
                            >
                                <option value="">Оберіть статус</option>
                                {statuses.map((st) => (
                                    <option key={st.id} value={st.id}>{st.value_name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAddDevice}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile */}
            {isMobile ? (
                <div className="operator-accordion-list">
                    {devices.map((device) => {
                        const isEditing = editingDeviceId === device.id;
                        const isExpanded = expandedDeviceId === device.id || isEditing;
                        const statusLabel = device.status_name || "Без статусу";

                        return (
                            <div key={device.id} className="operator-accordion-item config-card">
                                <button
                                    type="button"
                                    className="operator-accordion-head"
                                    onClick={() => toggleDeviceExpanded(device.id, isEditing)}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="operator-accordion-head-main">
                                        <div className="operator-accordion-title">{device.device_name || "Без назви"}</div>
                                        <div className="operator-accordion-meta">{statusLabel}</div>
                                    </div>
                                    <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                </button>

                                <OperatorCardActions
                                    editing={isEditing}
                                    onEdit={() => handleEdit(device)}
                                    onSave={() => handleSave(device.id)}
                                    onCancel={handleCancel}
                                />

                                {isExpanded ? (
                                    <div className="operator-accordion-body">
                            {/* Назва */}
                            <div>
                                <strong>Назва:</strong>{" "}
                                {isEditing ? (
                                    <input
                                        value={editedDevice.device_name}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, device_name: e.target.value })
                                        }
                                    />
                                ) : (
                                    device.device_name
                                )}
                            </div>

                            {/* Бренд */}
                            <div>
                                <strong>Бренд:</strong>{" "}
                                {isEditing ? (
                                    <input
                                        value={editedDevice.brand}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, brand: e.target.value })
                                        }
                                    />
                                ) : (
                                    device.brand
                                )}
                            </div>

                            {/* Локація */}
                            <div>
                                <strong>Локація:</strong>{" "}
                                {isEditing ? (
                                    <select
                                        value={editedDevice.location_id ?? ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, location_id: e.target.value })
                                        }
                                    >
                                        <option value="">Без локації</option>
                                        {locations.map((loc) => (
                                            <option key={loc.id} value={loc.id}>
                                                {buildLocationLabel(loc) || loc.location_name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    device.location_name || "Без локації"
                                )}
                            </div>

                            {/* Режим */}
                            <div>
                                <strong>Режим:</strong>{" "}
                                {isEditing ? (
                                    <select
                                        value={editedDevice.work_modes_id ?? ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, work_modes_id: e.target.value })
                                        }
                                    >
                                        <option value="">Без режиму</option>
                                        {workModes.map((wm) => (
                                            <option key={wm.id} value={wm.id}>
                                                {wm.work_mode_name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    device.work_mode_name || "Без режиму"
                                )}
                            </div>

                            {/* Розетка */}
                            <div>
                                <strong>Розетка:</strong>{" "}
                                {isEditing ? (
                                    <select
                                        value={editedDevice.socket_id ?? ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, socket_id: e.target.value })
                                        }
                                    >
                                        <option value="">Без розетки</option>
                                        {buildSelectableOutlets(sockets, availableSockets, editedDevice.socket_id).map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    device.socket_name || "Без розетки"
                                )}
                            </div>

                            {/* Опис */}
                            <div>
                                <strong>Опис:</strong>{" "}
                                {isEditing ? (
                                    <input
                                        value={editedDevice.descriptions || ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, descriptions: e.target.value })
                                        }
                                    />
                                ) : (
                                    device.descriptions || "-"
                                )}
                            </div>

                            {/* Ціна */}
                            <div>
                                <strong>Ціна:</strong>{" "}
                                {isEditing ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editedDevice.price || ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, price: e.target.value })
                                        }
                                    />
                                ) : (
                                    device.price
                                )}
                            </div>

                            {/* QR код */}
                            <div>
                                <strong>QR код:</strong>{" "}
                                {isEditing ? (
                                    <input
                                        value={editedDevice.qr_code || ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, qr_code: e.target.value })
                                        }
                                    />
                                ) : (
                                    device.qr_code || "-"
                                )}
                            </div>

                            {/* Статус */}
                            <div>
                                <strong>Статус:</strong>{" "}
                                {isEditing ? (
                                    <select
                                        value={editedDevice.status ?? ""}
                                        onChange={(e) =>
                                            setEditedDevice({ ...editedDevice, status: e.target.value })
                                        }
                                    >
                                        <option value="">Без статусу</option>
                                        {statuses.map((st) => (
                                            <option key={st.id} value={st.id}>
                                                {st.value_name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    statusLabel
                                )}
                            </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="table-responsive compact">
                    <table>
                        <thead>
                            <tr>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("device_name")}>
                                    Назва {sortBy === "device_name" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("brand")}>
                                    Бренд {sortBy === "brand" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_id")}>
                                    Локація {sortBy === "location_id" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("work_modes_id")}>
                                    Режим {sortBy === "work_modes_id" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("socket_id")}>
                                    Розетка {sortBy === "socket_id" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                                    Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("price")}>
                                    Ціна {sortBy === "price" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>QR код</th>
                                <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                                    Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                                </th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedData().map((device) => (
                                <tr key={device.id}>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <input
                                                value={editedDevice.device_name}
                                                onChange={(e) =>
                                                    setEditedDevice({ ...editedDevice, device_name: e.target.value })
                                                }
                                            />
                                        ) : (
                                            device.device_name
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <input
                                                value={editedDevice.brand}
                                                onChange={(e) =>
                                                    setEditedDevice({ ...editedDevice, brand: e.target.value })
                                                }
                                            />
                                        ) : (
                                            device.brand
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <select
                                                value={editedDevice.location_id ?? ""}
                                                onChange={(e) => setEditedDevice({ ...editedDevice, location_id: e.target.value })}
                                            >
                                                <option value="">Без локації</option>
                                                {locations.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>{buildLocationLabel(loc) || loc.location_name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            device.location_name || "Без локації"
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <select
                                                value={editedDevice.work_modes_id ?? ""}
                                                onChange={(e) => setEditedDevice({ ...editedDevice, work_modes_id: e.target.value })}
                                            >
                                                <option value="">Без режиму</option>
                                                {workModes.map((wm) => (
                                                    <option key={wm.id} value={wm.id}>{wm.work_mode_name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            device.work_mode_name
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <select
                                                value={editedDevice.socket_id ?? ""}
                                                onChange={(e) => setEditedDevice({ ...editedDevice, socket_id: e.target.value })}
                                            >
                                                <option value="">Без розетки</option>
                                                {buildSelectableOutlets(sockets, availableSockets, editedDevice.socket_id).map((s) => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            device.socket_name || "Без розетки"
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <input
                                                value={editedDevice.descriptions || ""}
                                                onChange={(e) =>
                                                    setEditedDevice({ ...editedDevice, descriptions: e.target.value })
                                                }
                                            />
                                        ) : (
                                            device.descriptions
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={editedDevice.price || ""}
                                                onChange={(e) =>
                                                    setEditedDevice({ ...editedDevice, price: e.target.value })
                                                }
                                            />
                                        ) : (
                                            device.price
                                        )}
                                    </td>
                                    <td>
                                        {editingDeviceId === device.id ? (
                                            <input
                                                value={editedDevice.qr_code || ""}
                                                onChange={(e) =>
                                                    setEditedDevice({ ...editedDevice, qr_code: e.target.value })
                                                }
                                            />
                                        ) : (
                                            device.qr_code
                                        )}
                                    </td>
                                    <td className="text-center">
                                        {editingDeviceId === device.id ? (
                                            <select
                                                value={editedDevice.status ?? ""}
                                                onChange={(e) => setEditedDevice({ ...editedDevice, status: e.target.value })}
                                            >
                                                <option value="">Без статусу</option>
                                                {statuses.map((st) => (
                                                    <option key={st.id} value={st.id}>{st.value_name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            device.status_name
                                        )}
                                    </td>
                                    <td className="actions">
                                        {editingDeviceId === device.id ? (
                                            <>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleSave(device.id)}
                                                    title="Зберегти"
                                                >
                                                    <Save className="icon-btn" />
                                                </button>
                                                <button
                                                    className="icon-button"
                                                    onClick={handleCancel}
                                                    title="Скасувати"
                                                >
                                                    <X className="icon-btn" />
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="icon-button"
                                                onClick={() => handleEdit(device)}
                                                title="Редагувати"
                                            >
                                                <Edit className="icon-btn" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DevicesOperators;
